import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';

import type { PopupListItem, RecordData, Service } from 'webapp-types';
import { arrayMove, sortArrayBasedOnSubset, sortBy } from 'webapp/utils/common';
import {
	clearTableCellWidthsOnDragStop,
	getSortableContainment,
	getSortableItemIndex,
	setTableCellWidthsOnDragStart,
} from 'webapp/utils/html';

import { containerEventName } from 'webapp/modifiers/visible-check-container';
import type { ReportCreateOrigin } from 'webapp/components/reports/popup-create-report';

import type {
	AddTagAction,
	DeleteAction,
	DeletePermanentlyAction,
	RestoreAction,
	ToggleFavoriteAction,
} from 'webapp/models-custom/batch-select';
import BatchSelect from 'webapp/models-custom/batch-select';
import { ReportCategory, ReportFilterId } from 'webapp/models-custom/filter-sort/report';
import type FilterSortReportInGrid from 'webapp/models-custom/filter-sort/report-in-grid';
import type FilterSortReportInList from 'webapp/models-custom/filter-sort/report-in-list';
import type Report from 'webapp/models-custom/report';
import type ResolutionCenterItem from 'webapp/models-custom/resolutioncenter-item';

import type { TrackingOriginTypeForSchedulePopup } from 'webapp/services/model-manager/reports';

export interface Args {
	transferedReportAccountTemplateNotifications: ResolutionCenterItem[];
	transferedReportPublicTemplateNotifications: ResolutionCenterItem[];
	hasReports: boolean;
	openView: (viewId: string) => void;
	onReportCreateChoice: (origin: ReportCreateOrigin) => void;
	opensCreateReportModal: boolean;
	updateIsRecentlyDeletedFilterActive: (isActive: boolean) => void;
}

type Action =
	ToggleFavoriteAction | AddTagAction | DeleteAction | DeletePermanentlyAction | RestoreAction;

type AddButtonAction = 'createReport' | 'useAccountTemplate' | 'usePublicTemplate';
type AddButtonOption = PopupListItem<AddButtonAction>;

export default class ReportsIndexBaseComponent extends Component<Args> {
	@service('popup') declare popupService: Service.Popup;
	@service('request') declare requestService: Service.Request;
	@service('store') declare storeService: Service.Store;
	@service('tour') declare tourService: Service.Tour;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('model-manager/notifications')
	declare mmNotificationsService: Service.ModelManager.Notifications;

	declare filterSort: FilterSortReportInGrid | FilterSortReportInList;

	visibleCheckContainerNode: HTMLElement | null = null;
	sortableElements: JQuery[] = [];
	addButtonText = 'New report';
	batchSelect: BatchSelect<Report>;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.batchSelect = new BatchSelect({
			getAllItems: () => {
				return this.filterSort.results;
			},
			getOptions: () => {
				return this.batchOptions;
			},
			doBatchAction: (actionName) => {
				return this.doBatchAction(actionName as DeleteAction);
			},
		});

		void this.mmNotificationsService.requestNotificationsForFeature('report');
	}

	@cached
	get model(): Report[] {
		return this.mmReportsService.normalReportModelsWithDeleted;
	}

	get trackingOrigin(): TrackingOriginTypeForSchedulePopup {
		return 'reports';
	}

	get noResults(): boolean {
		return !this.args.hasReports;
	}

	get sortedModel(): Report[] {
		return sortBy(this.model, 'order', 'numericId');
	}

	get addBtnShown(): boolean {
		return !this.noResults && !this.isRecentlyDeletedActive;
	}

	get addButtonContainerNode() {
		return this.addBtnShown && !this.selectedItems.length
			? this.navigationService.addButtonContainerNode
			: null;
	}

	get batchButtonContainerNode() {
		return this.selectedItems.length ? this.navigationService.batchButtonContainerNode : null;
	}

	get selectedItems(): Report[] {
		return this.batchSelect.selectedItems;
	}

	get areAllSelectedItemsFavorited(): boolean {
		return this.selectedItems.every((item) => item.favorite);
	}

	get batchOptions(): PopupListItem<Action>[] {
		const selectedCount = this.selectedItems.length,
			batchSelect = this.batchSelect;

		let options: PopupListItem<Action>[];

		if (this.filterSort.isRecentlyDeletedSelected) {
			options = [
				{
					class: 'delete-permanently',
					icon: 'Trash',
					text: `Permanently delete (${selectedCount})`,
					action: 'batchDeletePermanently',
					theme: 'red',
				},
				{
					icon: 'Restore',
					text: `Restore (${selectedCount})`,
					action: 'batchRestore',
				},
			];

			return options;
		}

		options = [
			batchSelect.toggleFavoriteOption,
			batchSelect.addTagsOption,
			batchSelect.deleteOption,
		];

		return options;
	}

	get addButtonOptions(): AddButtonOption[] {
		const options: AddButtonOption[] = [
			{
				icon: 'Plus',
				text: 'New report',
				action: 'createReport',
			},
			{
				icon: 'FolderUser',
				text: 'Use account template',
				action: 'useAccountTemplate',
			},
			{
				icon: 'FolderOpen',
				text: 'Use public template',
				action: 'usePublicTemplate',
			},
		];

		return options;
	}

	get isRecentlyDeletedActive() {
		return this.filterSort.isRecentlyDeletedSelected;
	}

	get isRecentlyDeletedActiveWithResults() {
		return this.filterSort.isRecentlyDeletedSelected && this.filterSort.results.length > 0;
	}

	get isRecentlyDeletedLoading(): boolean {
		return (
			this.filterSort.results.length === 0 && this.mmReportsService.isRecentlyDeletedLoading
		);
	}

	@cached
	get isNotificationsSectionVisible(): boolean {
		return (
			this.args.transferedReportAccountTemplateNotifications.length > 0 ||
			this.args.transferedReportPublicTemplateNotifications.length > 0
		);
	}

	@cached
	get isFavoriteSectionVisible(): boolean {
		return (
			this.favoriteReportsFilterFn(this.filterSort.results).length > 0 &&
			!this.isNotificationsSectionVisible
		);
	}

	@cached
	get isNonFavoriteSectionVisible(): boolean {
		return !this.isFavoriteSectionVisible && !this.isNotificationsSectionVisible;
	}

	initSortable(
		ctEl: JQuery,
		itemSelectorString: string,
		placeholderElementFn: (el: JQuery) => JQuery,
		sortingFavorites = false,
	): void {
		const containment = getSortableContainment(ctEl);

		ctEl.sortable({
			handle: '.drag',
			containment,
			placeholder: {
				element: placeholderElementFn,
				update: function () {
					return;
				},
			},
			forcePlaceholderSize: true,
			tolerance: 'pointer',
			items: `${itemSelectorString}:not(.notification)`,
			start: (_e: JQuery.Event, ui: jQueryUIInstance) => {
				setTableCellWidthsOnDragStart(ui);

				const startIndex = getSortableItemIndex(ui.item, itemSelectorString);
				ui.item.find('.drag').attr('data-position', startIndex.toString());
			},
			stop: (_e: JQuery.Event, ui: jQueryUIInstance) => {
				clearTableCellWidthsOnDragStop(ui);

				const handle = ui.item.find('.drag'),
					oldIndex = Number(handle.attr('data-position')),
					newIndex = getSortableItemIndex(ui.item, itemSelectorString);

				if (oldIndex !== newIndex) {
					this.updateSortOrder(oldIndex, newIndex, sortingFavorites);
				}
			},
		});

		this.sortableElements.push(ctEl);
	}

	destroySortable(): void {
		this.sortableElements.forEach((el) => {
			if (el?.length && el.sortable('instance')) {
				el.sortable('destroy');
			}
		});
		this.sortableElements = [];
	}

	updateSortOrder(oldIndex: number, newIndex: number, sortingFavorites: boolean): void {
		if (!sortingFavorites) {
			const favoriteItemsCount = this.sortedModel.filter((item) => item.favorite).length;
			oldIndex += favoriteItemsCount;
			newIndex += favoriteItemsCount;
		}

		const orderedIds = this.sortedModel.map((m) => m.numericId),
			filterOrderedIds = arrayMove(
				this.filterSort.results.map((m) => m.numericId!),
				oldIndex,
				newIndex,
			),
			newSortOrder = sortArrayBasedOnSubset(orderedIds, filterOrderedIds);

		this.requestService
			.api<RecordData.Payload>('POST', '/reports/order', newSortOrder)
			.then((reply) => {
				this.storeService.pushPayload(reply);
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));

		this.model.forEach((item) => {
			item.order = newSortOrder.indexOf(item.numericId);
			item.applyBufferedChanges(['order']); // we won't be sending save
		});
	}

	doBatchAction(actionName: DeleteAction) {
		return this[actionName]();
	}

	@action
	batchToggleFavorite() {
		if (this.areAllSelectedItemsFavorited) {
			return this.mmReportsService.batchUnfavorite(this.selectedItems);
		} else {
			return this.mmReportsService.batchFavorite(this.selectedItems);
		}
	}

	@action
	batchUpdateTags() {
		return this.mmReportsService.batchUpdateTags(this.selectedItems);
	}

	@action
	batchDelete() {
		return this.mmReportsService.batchDelete(this.selectedItems);
	}

	@action
	batchDeletePermanently() {
		const isRecentlyDeletedActive = this.isRecentlyDeletedActive;
		const remainingItemsAfterDelete =
			this.filterSort.results.length - this.selectedItems.length;

		return this.mmReportsService.batchDeletePermanently(this.selectedItems).then((result) => {
			if (isRecentlyDeletedActive && remainingItemsAfterDelete === 0) {
				this.resetRecentlyDeletedFilter();
			}
			return result;
		});
	}

	@action
	deleteAllPermanently() {
		const isRecentlyDeletedActive = this.isRecentlyDeletedActive;

		return this.mmReportsService
			.batchDeletePermanently(this.filterSort.results)
			.then((result) => {
				if (isRecentlyDeletedActive) {
					this.resetRecentlyDeletedFilter();
				}
				return result;
			});
	}

	@action
	batchRestore() {
		const isRecentlyDeletedActive = this.isRecentlyDeletedActive;
		const remainingItemsAfterRestore =
			this.filterSort.results.length - this.selectedItems.length;

		return this.mmReportsService.batchRestore(this.selectedItems).then((result) => {
			if (isRecentlyDeletedActive && remainingItemsAfterRestore === 0) {
				this.resetRecentlyDeletedFilter();
			}
			return result;
		});
	}

	@action
	triggerContentChange(node: HTMLElement): void {
		// for visible-check
		node.dispatchEvent(new Event(containerEventName));
	}

	@action
	favoriteReportsFilterFn(items: Report[]): Report[] {
		return items.filter((item: Report) => item.favorite);
	}

	@action
	nonFavoriteReportsFilterFn(items: Report[]): Report[] {
		return items.filter((item: Report) => !item.favorite);
	}

	@action
	doAddButtonAction(actionName: string) {
		return this[actionName as AddButtonAction]();
	}

	@action
	createReport() {
		this.args.onReportCreateChoice('reports_new');
	}

	@action
	useAccountTemplate() {
		this.navigationService.transitionToRoute('reports.account-templates');
	}

	@action
	usePublicTemplate() {
		this.navigationService.transitionToRoute('reports.public-templates');
	}

	resetRecentlyDeletedFilter(): void {
		const categoryFilter = this.filterSort.findFilter(ReportFilterId.CATEGORY);
		if (categoryFilter) {
			categoryFilter.selectOption(ReportCategory.ALL);
		}
	}

	@action
	handleSingleItemPermanentDelete(): void {
		if (this.isRecentlyDeletedActive && this.filterSort.results.length === 1) {
			this.resetRecentlyDeletedFilter();
		}
	}

	@action
	handleSingleItemRestore(): void {
		if (this.isRecentlyDeletedActive && this.filterSort.results.length === 1) {
			this.resetRecentlyDeletedFilter();
		}
	}
}
